import { getGoogleDriveMediaUrl } from '../utils/getGoogleDriveMediaUrl';
import SmartImage from '../utils/SmartImage';
import AdSpace from '../components/AdSpace';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, CircularProgress, Alert, Modal, IconButton, Fade, Zoom } from '@mui/material';
import { ArrowBack, Edit, Close } from '@mui/icons-material';
import { newsService } from '../../firebase/services';

/**
 * Componente para exibir uma notícia completa
 * 
 * Recebe o ID da notícia pela URL e exibe o conteúdo completo
 */
export default function NoticiaDetalhada() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [noticia, setNoticia] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageModalOpen, setImageModalOpen] = useState(false);

    useEffect(() => {
        const fetchNoticia = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const noticiaData = await newsService.getNewsById(id);
                setNoticia(noticiaData);
            } catch (error) {
                console.error('Erro ao buscar notícia:', error);
                setError('Notícia não encontrada ou erro ao carregar');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchNoticia();
        }
    }, [id]);

    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('pt-BR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return dateString || 'Data não disponível';
        }
    };

    if (loading) {
        return (
            <Container sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ mt: 2 }}>
                    Carregando notícia...
                </Typography>
            </Container>
        );
    }

    if (error || !noticia) {
        return (
            <Container sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error || 'Notícia não encontrada'}
                </Alert>
                <Box sx={{ textAlign: 'center' }}>
                    <Button 
                        variant="contained" 
                        onClick={() => navigate('/')}
                        startIcon={<ArrowBack />}
                    >
                        Voltar ao início
                    </Button>
                </Box>
            </Container>
        );
    }

    const isAdmin = typeof window !== 'undefined' && localStorage.getItem('admin_uid');
    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Botão de voltar e editar */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                {isAdmin && (
                    <Button 
                        variant="contained" 
                        onClick={() => navigate(`/edit-news/${id}`)}
                        startIcon={<Edit />}
                        sx={{
                            backgroundColor: '#0b2951',
                            '&:hover': {
                                backgroundColor: '#0a2347'
                            }
                        }}
                    >
                        Editar
                    </Button>
                )}
            </Box>

                {/* Título */}
                <Typography 
                    variant="h3" 
                    component="h1" 
                    sx={{ 
                        fontFamily: "Atlan",
                        fontWeight: "bold",
                        mb: 2,
                        lineHeight: 1.2
                    }}
                >
                    {noticia.title}
                </Typography>

                {/* Subtítulo */}
                <Typography 
                    variant="h5" 
                    sx={{ 
                        fontFamily: "Atlan",
                        color: 'text.secondary',
                        mb: 3,
                        lineHeight: 1.4
                    }}
                >
                    {noticia.subtitle}
                </Typography>

                {/* Imagem/Vídeo */}
                <Box 
                    sx={{
                        width: '100%',
                        borderRadius: 2,
                        mb: 2,
                        border: '1px solid #e0e0e0',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#f5f5f5',
                        minHeight: 200,
                        cursor: 'pointer'
                    }}
                    onClick={() => setImageModalOpen(true)}
                >
                    {noticia && (
                        <SmartImage
                            src={getGoogleDriveMediaUrl(noticia.mediaUrl, noticia.driveFileId, undefined, noticia.thumbnailUrl)}
                            alt={noticia.title}
                            driveFileId={noticia.driveFileId}
                            style={{
                                width: '100%',
                                maxHeight: 400,
                                objectFit: 'cover',
                                borderRadius: 12,
                                display: 'block',
                                background: '#e0e0e0'
                            }}
                        />
                    )}
                </Box>

                {/* Data e Autor */}
                <Box sx={{ mb: 4, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography 
                        variant="caption" 
                        sx={{ 
                            bgcolor: 'primary.main', 
                            height: 16,
                            color: 'white', 
                            px: 1.5, 
                            py: 0.25, 
                            borderRadius: 2,
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                        }}
                    >
                        {noticia.category || 'Geral'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        {noticia.author || 'Redação Arena Esporte Total'} •  {formatDate(noticia.createdAt)}
                    </Typography>
                </Box>

                {/* Conteúdo */}
                <Typography 
                    variant="body1" 
                    sx={{ 
                        fontFamily: "Atlan",
                        fontSize: 18,
                        lineHeight: 1.8,
                        whiteSpace: 'pre-line',
                        color: 'text.primary'
                    }}
                >
                    {noticia.fullContent}
                </Typography>

                {/* Espaço publicitário após conteúdo */}
                <AdSpace 
                    height="100px"
                    sx={{ 
                        marginY: 4
                    }}
                />

                {/* Botão de voltar no final */}
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Button 
                        onClick={() => navigate('/')}
                        startIcon={<ArrowBack />}
                    >
                        Voltar às notícias
                    </Button>
                </Box>

                {/* Modal para imagem em tela cheia */}
                <Modal
                    open={imageModalOpen}
                    onClose={() => setImageModalOpen(false)}
                    closeAfterTransition
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2
                    }}
                >
                    <Fade in={imageModalOpen} timeout={400}>
                        <Box
                            sx={{
                                position: 'relative',
                                maxWidth: '90vw',
                                maxHeight: '90vh',
                                outline: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {/* Botão de fechar */}
                            <Fade in={imageModalOpen} timeout={600}>
                                <IconButton
                                    onClick={() => setImageModalOpen(false)}
                                    sx={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        color: 'white',
                                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.9)'
                                        },
                                        zIndex: 3,
                                        width: 40,
                                        height: 40,
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <Close />
                                </IconButton>
                            </Fade>
                            
                            {/* Imagem em tela cheia */}
                            <Zoom in={imageModalOpen} timeout={500}>
                                <Box>
                                    {noticia && (
                                        <SmartImage
                                            src={getGoogleDriveMediaUrl(noticia.mediaUrl, noticia.driveFileId, undefined, noticia.thumbnailUrl)}
                                            alt={noticia.title}
                                            driveFileId={noticia.driveFileId}
                                            style={{
                                                maxWidth: '90vw',
                                                maxHeight: '90vh',
                                                width: 'auto',
                                                height: 'auto',
                                                objectFit: 'contain',
                                                borderRadius: 8,
                                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                                                transition: 'transform 0.3s ease'
                                            }}
                                        />
                                    )}
                                </Box>
                            </Zoom>
                        </Box>
                    </Fade>
                </Modal>
            </Container>
    );
}
